import { useEffect, useState } from 'react';
import {
	Box,
	Button,
	HStack,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	SimpleGrid,
	Text,
} from '@chakra-ui/react';

import { Banner } from '../_ui/components';
// The CRM's date picker and the colour-mode-aware time input — each renders
// its own label, so no <Field> wrapper here (that would nest two labels).
import { DateField } from '../_ui/DateField';
import { TimeField } from '../_ui/TimeField';
import { useCampaignUI } from '../_ui/ui';

/**
 * Move a paused campaign's remaining messages to new dates or a new daily window.
 *
 * ★ IT ONLY EVER MOVES WHAT HAS NOT LEFT. The server re-spreads `SCHEDULED`
 * rows and nothing else — anything already sent keeps its day, because that is
 * a record of what a customer received rather than a plan. The dialog says so
 * before the click, since "reschedule" reads like it might recall messages.
 *
 * ★ THE WINDOW IS THE RECIPIENT'S, NOT THE ADMIN'S. 10:00–20:00 means ten in
 * the morning where each person lives, which is why a Pakistani and an Emirati
 * contact on the same day go out an hour apart. Worth saying on the form: the
 * alternative reading — the admin's own clock — is the intuitive one and wrong.
 *
 */
export default function RescheduleDialog({ isOpen, onClose, campaign, isLoading, onConfirm }) {
	const ui = useCampaignUI();
	const schedule = campaign?.schedule ?? {};

	const [form, setForm] = useState(() => ({
		startDate: schedule.startDate ?? '',
		endDate: schedule.endDate ?? '',
		windowStart: schedule.windowStart ?? '09:00',
		windowEnd: schedule.windowEnd ?? '20:00',
	}));

	// Reopening for a campaign whose dates changed must not show the old ones.
	useEffect(() => {
		if (!isOpen) return;
		setForm({
			startDate: schedule.startDate ?? '',
			endDate: schedule.endDate ?? '',
			windowStart: schedule.windowStart ?? '09:00',
			windowEnd: schedule.windowEnd ?? '20:00',
		});
	}, [isOpen, schedule.startDate, schedule.endDate, schedule.windowStart, schedule.windowEnd]);

	// Both fields hand back the string itself ('YYYY-MM-DD', 'HH:mm'), not an event.
	const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

	// The same three rules the server enforces, checked here only so the answer
	// arrives while the field is still under the cursor. The server remains the
	// authority — it refuses with `INVALID_SCHEDULE` and the exact problems.
	const problems = [];
	if (!form.startDate || !form.endDate) problems.push('Both dates are needed.');
	else if (form.endDate < form.startDate) problems.push('The end date is before the start date.');
	if (!form.windowStart || !form.windowEnd) problems.push('Both daily delivery times are needed.');
	if (form.windowStart >= form.windowEnd) problems.push('The daily window ends before it starts.');

	const unchanged =
		form.startDate === (schedule.startDate ?? '') &&
		form.endDate === (schedule.endDate ?? '') &&
		form.windowStart === (schedule.windowStart ?? '09:00') &&
		form.windowEnd === (schedule.windowEnd ?? '20:00');

	return (
		<Modal isOpen={isOpen} onClose={isLoading ? () => {} : onClose} closeOnOverlayClick={!isLoading} closeOnEsc={!isLoading} isCentered size='lg'>
			<ModalOverlay />
			<ModalContent bg={ui.surface}>
				<ModalHeader color={ui.textPrimary} fontSize='md'>
					Reschedule what is left
				</ModalHeader>
				<ModalCloseButton color={ui.textSecondary} isDisabled={isLoading} />

				<ModalBody>
					<Text fontSize='sm' color={ui.textSecondary} mb={4}>
						Messages that have already been sent keep their date and are not touched. Only the ones still
						waiting are moved.
					</Text>

					<SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
						<DateField label='First day' value={form.startDate} onChange={set('startDate')} maxDate={form.endDate || undefined} />
						<DateField label='Last day' value={form.endDate} onChange={set('endDate')} minDate={form.startDate || undefined} />
						<TimeField label='Send from' help='In each recipient’s own time zone' value={form.windowStart} onChange={set('windowStart')} />
						<TimeField label='Send until' help='In each recipient’s own time zone' value={form.windowEnd} onChange={set('windowEnd')} />
					</SimpleGrid>

					{problems.length > 0 && (
						<Box mt={4}>
							<Banner status='warning' description={problems.join(' ')} />
						</Box>
					)}

					{/* Narrowing the range can leave somebody with no day at all — the
					    server marks those SKIPPED rather than keeping a row that can
					    never send, and the count comes back in the result. */}
					{problems.length === 0 && !unchanged && (
						<Box mt={4}>
							<Banner
								status='info'
								description='Anyone whose new window has already passed where they live will be skipped, and you will be told how many.'
							/>
						</Box>
					)}
				</ModalBody>

				<ModalFooter>
					<HStack spacing={2}>
						<Button size='sm' variant='ghost' color={ui.textSecondary} onClick={onClose} isDisabled={isLoading}>
							Cancel
						</Button>
						<Button
							size='sm'
							
							
							isLoading={isLoading}
							isDisabled={problems.length > 0 || unchanged}
							onClick={() => onConfirm(form)} variant='brand'
						>
							Move the rest
						</Button>
					</HStack>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}
